import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import styles from './ImageListBlock.module.scss';
import i18n from '../../i18n';

import Image from '../Image';

const ImageListBlock = ({ images }) => {
    const [modalIsOpen, setModalIsOpen] = useState(false);
    const [currentImage, setCurrentImage] = useState(0);
    const modalRef = useRef(null);

    const openModal = (imageId) => {
        setModalIsOpen(true);
        setCurrentImage(() => imageId);
    };

    const closeModal = () => {
        setModalIsOpen(false);
        setCurrentImage(() => 0);
    };

    const ESC = 27;

    useEffect(() => {
        const kb = (e) => {
            if (e.keyCode === ESC) {
                closeModal();
            }
        };
        window.addEventListener('keyup', kb);
        return () => window.removeEventListener('keyup', kb);
    }, []);

    const handleFocusTrap = (event) => {
        if (event.shiftKey && event.key === 'Tab') {
            if (document.activeElement) {
                event.preventDefault();
            }
        } else if (!event.shiftKey && event.key === 'Tab') {
            if (document.activeElement) {
                event.preventDefault();
            }
        }
    };

    useEffect(() => {
        if (modalIsOpen) {
            document.addEventListener('focus', handleFocusTrap, true);
        }

        return () => {
            document.removeEventListener('focus', handleFocusTrap, true);
        };
    }, [modalIsOpen]);

    return (
        <>
            <div className={styles['ImageListBlock']}>
                <div className={styles['ImageListBlock__Content']}>
                    {images.map((item, i) => (
                        <Item
                            imageId={i}
                            nrOfItems={images.length}
                            key={i}
                            {...item}
                            onClick={() => openModal(i)}
                            modalIsOpen={modalIsOpen}
                        />
                    ))}
                </div>
            </div>
            {modalIsOpen && (
                <div
                    ref={modalRef}
                    tabIndex={-1}
                    className={styles['ImageListBlock__Modal']}
                >
                    <button
                        onClick={(e) => closeModal(e)}
                        className={styles['ImageListBlock__Modal__Close']}
                        aria-label={i18n.t('imageGallery.close')}
                        tabIndex={0}
                    />
                    {/* <span className="sr-only">
                            {i18n.t('imageGallery.close')}
                        </span> */}

                    {images.map((item, i) => (
                        <ModalImage
                            key={i}
                            imageId={i}
                            currentImage={currentImage}
                            closeModal={(e) => closeModal(e)}
                            {...item}
                        />
                    ))}
                </div>
            )}
        </>
    );
};

const Item = ({ image, imageId, nrOfItems, onClick, modalIsOpen }) => {
    let modifier;

    if (nrOfItems === 3) {
        modifier = 'Three';
    } else if (nrOfItems >= 4) {
        modifier = 'Four';
    }

    const itemClass = classNames(styles['ImageListBlock__Item'], {
        [styles[`ImageListBlock__Item--${modifier}`]]: !!modifier,
    });

    return (
        <button
            className={itemClass}
            aria-label={i18n.t('imageGallery.zoom')}
            onClick={() => onClick(imageId)}
            tabIndex={modalIsOpen ? -1 : 0}
        >
            <span className={styles['ImageListBlock__Item__Image']}>
                <span className="sr-only">{i18n.t('imageGallery.close')}</span>
                <div className={styles['ImageListBlock__Item__Overlay']} />
                <Image {...image} />
            </span>
        </button>
    );
};

const ModalImage = ({ image, imageId, currentImage }) => {
    if (currentImage !== imageId) {
        return null;
    }

    return (
        <div
            className={styles['ImageListBlock__Modal__Image']}
            onClick={() => close()}
        >
            <Image {...image} />
        </div>
    );
};

ImageListBlock.propTypes = {
    images: PropTypes.array,
};

ImageListBlock.defaultProps = {
    images: [],
};

Item.propTypes = {
    image: PropTypes.object,
    imageId: PropTypes.number,
    nrOfItems: PropTypes.number,
    onClick: PropTypes.func,
    modalIsOpen: PropTypes.bool,
};

Item.defaultProps = {
    image: {},
    imageId: 0,
    nrOfItems: 0,
    modalIsOpen: false,
};

ModalImage.propTypes = {
    image: PropTypes.object,
    imageId: PropTypes.number,
    currentImage: PropTypes.string,
    onClick: PropTypes.func,
    closeModal: PropTypes.func,
};

ModalImage.defaultProps = {
    image: {},
    imageId: 0,
    currentImage: '',
    closeModal: () => {},
};

export default ImageListBlock;
